/**
 * Who made a staged change: OMNI or the underwriter.
 *
 * One component for the banner and for every staged row, so the banner's
 * labelled badges read as the legend for the icon-only ones in the grid. Rows
 * get the icon alone because the pair it sits beside lives in a one-unit
 * actions column and beside global labels, both sized for the tick and cross.
 */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles, faUser } from '@fortawesome/pro-regular-svg-icons';
import {
  SOURCE_DESCRIPTION,
  SOURCE_LABEL,
  type ChangeSource,
} from './proposedChangesModel';
import classes from './ProvenanceBadge.module.css';

const ICON = { assistant: faSparkles, operator: faUser } as const;

export interface ProvenanceBadgeProps {
  readonly source: ChangeSource;
  /**
   * How many, in the banner, where the badge is labelled. Absent on a row,
   * where it is the icon alone.
   */
  readonly count?: number;
}

const ProvenanceBadge = ({ source, count }: ProvenanceBadgeProps) => {
  const description =
    count === undefined
      ? SOURCE_DESCRIPTION[source]
      : `${count} from ${SOURCE_LABEL[source]}`;
  return (
    // A native title rather than the design-system tooltip: this renders on
    // every staged row, inside grid components that do not otherwise need a
    // Mantine provider.
    <span
      className={`${classes.badge} ${classes[source]} ${
        count === undefined ? classes.iconOnly : ''
      }`}
      role="img"
      aria-label={description}
      title={description}
    >
      <FontAwesomeIcon icon={ICON[source]} className={classes.icon} />
      {count !== undefined && (
        <>
          {SOURCE_LABEL[source]}
          <span className={classes.count}>{count}</span>
        </>
      )}
    </span>
  );
};

export default ProvenanceBadge;
